import { useState } from "react";
import {
  cancelTransaction,
  getPaymentStatus,
  initiatePOSPayment,
} from "../../../../apis";
import { failureToast } from "services/http";
import { maskEmail, maskPhoneNumber } from "../../../paymentLink/utils";
import { successToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";

const buildMessage = ({ email, contactNumber }) => {
  const channels = [email && "Email", contactNumber && "SMS"]
    .filter(Boolean)
    .join(" and ");

  return `Payment link has been sent via ${channels}.`;
};

const successAlert = (notification) => {
   if(notification?.isAXSB) return ;
  successToast(buildMessage(notification));
};

const getLinkRefNumber = ({ email, contactNumber }) => {
  if (contactNumber) return maskPhoneNumber(contactNumber);
  if (email) return maskEmail(email);
  return "N/A";
};
const useInitiatePayment = (
  setBalanceAmount,
  setBalanceMode,
  setCollectPayment,
  paymentMode,
  bankMasterId
) => {
  const [responseData, setResponse] = useState({});
  const [loader, setLoader] = useState(false);
  const cardType = {
    creditCard: "Credit card",
    debitCard: "Debit card",
    Link: "Via Link",
    AXSB:"AXSB"
  };
  const apiKey = { creditCard: "CC", debitCard: "DB", Link: "Link", AXSB:"AXSB" };
  const vendorTypeMap = {
    paytm: "PAYTM_POS",
    pinelabs: "PINELAB_POS",
    link: "PAYTM_LINK",
    "axis sound box": "AXIS_BANK_BOX",
  };

  const initiatePayment = async (data, amount, vendorType, state, setState) => {
    setLoader(true);
    const notification = vendorType?.notification
      ? { ...vendorType?.notification }
      : {};
    const { applicationQuotationId, buId, bu, applicationId, psid } = data;

    const request = {
      applicationQuotationId,
      applicationId,
      psid,
      businessAreaId: buId,
      businessAreaDispValue: bu,
      amount,
      totalAmountSelected: amount,
      paymentModeCollectionRequestList: [
        {
          paymentModeId: bankMasterId[apiKey[paymentMode]],
          paymentMode: apiKey[paymentMode], // Credit Card
          paidAmount: amount,
        },
      ],
      notificationInfo: {
        ...notification,
      },
    };

    try {
      // Step 1: Initiate payment
      const vendorTypeKey = vendorTypeMap[vendorType?.value?.toLowerCase()];
      console.log(vendorType?.value?.toLowerCase());

      const initiateResponse = await initiatePOSPayment(
        request,
        vendorTypeKey,
        state
      );

      if (!initiateResponse || initiateResponse.code !== 200) {
        handleError("Payment initiation failed");
        return;
      }
      if (initiateResponse.code === 412) {
        failureToast(initiateResponse?.message);
        setTimeout(function () {
          window.location.reload();
        }, 4000);
        return;
      }
      setState(initiateResponse?.state);

      const paymentModeCollectionDetailsId =
        initiateResponse.data?.paymentModeCollectionDetailsId;
      if (!paymentModeCollectionDetailsId)
        throw new Error("Failed to retrieve transaction ID");

      setResponse({
        clientTransactionId: initiateResponse.data?.clientTransactionId,
        amount: amount,
        businessAreaId: buId,
      });

      if (Object.keys(request?.notificationInfo)?.length > 0)
        successAlert(request?.notificationInfo);

      // Step 2: Poll transaction status with initial 30s wait and then 5s interval
      pollTransactionStatus(
        paymentModeCollectionDetailsId,
        30000,
        5000,
        amount,
        initiateResponse.data?.clientTransactionId,
        {
          vendorTypeKey,
          businessAreaId: buId,
          notification,
        }
      ); // 30 seconds initial delay, then 5 seconds intervals
    } catch (error) {
      handleError("Payment process failed", error);
    }
  };

  const pollTransactionStatus = async (
    paymentModeCollectionDetailsId,
    initialDelay,
    interval,
    amount,
    txnId,
    rest
  ) => {
    const { vendorTypeKey, businessAreaId, notification } = rest;
    setTimeout(async function poll() {
      try {
        const statusResponse = await getPaymentStatus(
          txnId,
          vendorTypeKey,
          businessAreaId
        );
        const status = statusResponse?.data?.status;
        const cardNumber =
          paymentMode === "Link"
            ? getLinkRefNumber(notification)
            : `XXXX${statusResponse?.data?.additionalDetails?.cardNumber}`;

        if (statusResponse?.data?.code === 400) {
          setResponse({ data: { status: "FAILED" } });
          setLoader(false);
          return;
        }

        if (["PAID", "FAILED", "CANCELLED"].includes(status)) {
          setResponse(statusResponse);
          if (status === "PAID") {
            setBalanceAmount((currentBal) => currentBal - amount);
            setCollectPayment((currentCollectedPaymentList) => [
              ...currentCollectedPaymentList,
              {
                paidAmount: amount,
                paymentMode: apiKey[paymentMode],
                paymentModeDesc: `${cardType[paymentMode]}`,
                paymentModeId: bankMasterId[apiKey[paymentMode]],
                txnId: paymentModeCollectionDetailsId,
                isRemoveFromRequest: true,
                referenceNumber: cardNumber,
                isLink: paymentMode === "Link",
              },
            ]);

            setBalanceMode(true);
          }

          setLoader(false);
        } else {
          setTimeout(poll, interval); // Retry after the given interval
        }
      } catch (error) {
        handleError("Error during polling", error);
      }
    }, initialDelay);
  };

  const handleError = (message, error) => {
    console.error(message, error);
    setResponse({ status: "failure", message });
    setLoader(false);
  };

  const cancelLinkTransaction = async (request, loaderState) => {
    loaderState(true);
    const response = await cancelTransaction(request);
    if (response?.code === 200) {
      setResponse(response);
      loaderState(false);
      setLoader(false);
    } else {
      setResponse(response.data);
      loaderState(false);
      setLoader(false);
      failureToast("Failed to cancel the transaction");
    }
  };

  return {
    initiatePayment,
    responseData,
    loader,
    setResponse,
    cancelLinkTransaction,
  };
};

export default useInitiatePayment;
